import { Capacitor, registerPlugin } from '@capacitor/core';

export type NativeClerkOAuthProvider = 'apple' | 'google';

export interface NativeClerkEmailAddress {
  emailAddress: string;
}

export interface NativeClerkUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  imageUrl: string | null;
  primaryEmailAddress: NativeClerkEmailAddress | null;
  unsafeMetadata: Record<string, unknown>;
}

export interface NativeClerkSnapshot {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: NativeClerkUser | null;
}

export interface NativeClerkGetTokenOptions {
  template?: string;
  skipCache?: boolean;
}

export interface NativeClerkUpdateUserOptions {
  firstName?: string | null;
  lastName?: string | null;
  unsafeMetadata?: Record<string, unknown>;
}

export interface NativeClerkPlugin {
  configure(options: { publishableKey: string }): Promise<NativeClerkSnapshot>;
  getSnapshot(): Promise<NativeClerkSnapshot>;
  signInWithOAuth(options: { provider: NativeClerkOAuthProvider }): Promise<NativeClerkSnapshot>;
  signInWithPassword(options: { identifier: string; password: string }): Promise<NativeClerkSnapshot>;
  signOut(): Promise<NativeClerkSnapshot>;
  getToken(options?: NativeClerkGetTokenOptions): Promise<{ token: string | null }>;
  updateUser(options: NativeClerkUpdateUserOptions): Promise<NativeClerkSnapshot>;
  reloadUser(): Promise<NativeClerkSnapshot>;
  openUserProfile(): Promise<void>;
}

const unavailableNativeClerk = new Proxy(
  {},
  {
    get:
      () =>
      async (): Promise<never> => {
        throw new Error('Native Clerk auth is not available on this platform.');
      },
  }
) as NativeClerkPlugin;

export const NativeClerk =
  typeof registerPlugin === 'function'
    ? registerPlugin<NativeClerkPlugin>('ClerkNativeAuth')
    : unavailableNativeClerk;

export const isNativeClerkPlatform = (): boolean =>
  Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
